// ─────────────────────────────────────────────────────────────────────────────
// ROOT CAUSE OF THE CRASH:
//
//   The previous fix put this in App.js at module level:
//
//     if (typeof window === 'undefined') {
//       Header = require('remoteApp/header').default;  // ← THIS IS THE BUG
//     }
//
//   `import App from "../shared/App"` in server/index.js is a STATIC ES import.
//   Webpack executes ALL static imports synchronously during bundle parse —
//   BEFORE any of your code runs, including initFederation(). So App.js's
//   module-level require('remoteApp/header') fires into a completely
//   uninitialized federation container, giving:
//
//     "Cannot read properties of undefined (reading 'call')"
//     at webpack/runtime/create fake namespace object
//
//   The federation container modules are registered but their factory functions
//   are `undefined` at this point because init() has not been called yet.
//
// THE CORRECT FIX:
//   App.js must NEVER touch remoteApp at module load time on the server.
//   Instead:
//     - server/index.js loads RemoteHeader AFTER initFederation() completes
//     - server/index.js pre-renders it to a string (renderToStaticMarkup) and
//       passes that string to App as `headerHtml`
//     - App.js drops it into a div via dangerouslySetInnerHTML (see the
//       "BROWSER-SIDE HEADER TAKEOVER" block below for why, and how the
//       browser later takes over that same node once the remote chunk loads)
//
//   This way the federation container is always initialized before any
//   require('remoteApp/...') is called.
// ─────────────────────────────────────────────────────────────────────────────

// ── ALL IMPORTS AT THE TOP ────────────────────────────────────────────────────
import React, { Component } from "react";
import { Routes, Route, BrowserRouter } from 'react-router-dom';
import { Provider } from "./context";
import Cookies from "js-cookie";
import { Helmet } from 'react-helmet-async';
import routes from "./routes";
import "./App.css";
import "./Font.css";
import Footer from './footer/Footer';
import TagManager from 'react-gtm-module';
import { connect, Provider as ReduxProvider, ReactReduxContext } from "react-redux";
import dotenv from 'dotenv';
dotenv.config();

const appjson = require('../../app.json');

// ── ENVIRONMENT CONFIG ────────────────────────────────────────────────────────
const myenv = API_URLNEW; // injected by webpack DefinePlugin

let envConfig;
switch (myenv) {
  case 'production':    envConfig = appjson.apps[0].env_production;    break;
  case 'staging':       envConfig = appjson.apps[0].env_staging;        break;
  case 'preproduction': envConfig = appjson.apps[0].env_preproduction;  break;
  default:              envConfig = appjson.apps[0].env;
}

if (typeof global !== 'undefined') {
  global.hosturl      = envConfig.HOST_URL     || '';
  global.api_ci_url   = envConfig.API_CI_URL   || '';
  global.api_url      = envConfig.API_URL       || '';
  global.fcapi_url    = envConfig.FCAPI_URL     || '';
  global.redirect_url = envConfig.REDIRECT_URL  || '';
  global.loginUrl     = envConfig.LOGIN_URL     || '';
  global.registerUrl  = envConfig.REGISTER_URL  || '';
  global.logoutUrl    = envConfig.LOGOUT_URL    || '';
  global.jarvis_url   = envConfig.JARVIS_URL    || '';
}

// ── BROWSER-SIDE HEADER TAKEOVER ──────────────────────────────────────────────
// ONLY set up when running in the browser. On the server, nothing here runs —
// the server pre-renders the header to a plain HTML string instead (see
// server/index.js's use of renderToStaticMarkup + the `headerHtml` prop).
//
// Why not React.lazy() + Suspense (the previous approach): with plain
// renderToString() SSR (no streaming), hydrating a Suspense boundary whose
// lazy component hasn't loaded yet does NOT preserve the server-rendered DOM —
// React discards it and shows the fallback until the chunk resolves, then
// swaps in the real content. That produced a visible
// header-appears -> disappears -> reappears-with-login-info flicker.
//
// Instead: the header's SSR markup is injected via dangerouslySetInnerHTML
// (mountRemoteHeader below, wired up in App#componentDidMount). React treats
// dangerouslySetInnerHTML content as opaque — it does not diff or replace it
// during hydration, so the server-rendered header just sits there, untouched,
// for as long as it takes the remote chunk to load. Once the chunk resolves,
// we HYDRATE that same DOM node with its own independent root (hydrateRoot,
// not createRoot — createRoot wipes the container and mounts fresh, which
// would tear out the real SSR content and briefly show the component's own
// bare first-client-render output instead; hydrateRoot attaches to and reuses
// the existing markup). One deliberate takeover instead of a
// React-managed discard-then-rebuild.
let mountRemoteHeader = null;
if (typeof window !== 'undefined') {
  const headerImportPromise = import('remoteApp/headernew');
  mountRemoteHeader = (props, reduxStore) => {
    headerImportPromise.then((mod) => {
      const HeaderComponent = mod.default || mod;
      const node = document.getElementById('federated-header-root');
      if (!node) return;
      // hydrateRoot (not createRoot!) — node already has the real, server-rendered
      // header markup in it. createRoot().render() is a plain client render: it
      // wipes the container and mounts fresh, which discarded the SSR content and
      // replaced it with the component's own bare first-render output (causing the
      // header to visibly disappear). hydrateRoot attaches to the existing DOM
      // and reuses it instead of tearing it down.
      //
      // BrowserRouter + ReduxProvider wrappers are required here: hydrateRoot
      // creates a brand new, independent React root — it does NOT inherit the
      // <BrowserRouter>/<Provider> context from the outer app's root (context
      // never crosses root boundaries). The header uses useLocation() and its
      // own connect() (react-redux) internally, both of which throw without
      // their respective ancestor. Server-side, the equivalent static render is
      // wrapped in <StaticRouter>/<Provider> for the same reason (server/index.js).
      // ReduxProvider only actually satisfies the header's connect() because
      // 'react-redux' is now a shared singleton in webpack.config.js — otherwise
      // the header would be running its own separate react-redux module with
      // its own separate Context object, and this Provider wouldn't be visible
      // to it even though it's structurally an ancestor.
      const { hydrateRoot } = require('react-dom/client');
      hydrateRoot(
        node,
        <ReduxProvider store={reduxStore}>
          <BrowserRouter><HeaderComponent {...props} /></BrowserRouter>
        </ReduxProvider>
      );
    }).catch((err) => {
      console.error('Failed to load remote header:', err);
    });
  };
}

// ── APP COMPONENT ─────────────────────────────────────────────────────────────
class App extends Component {
  // Gives this class component direct access to the real Redux store instance
  // (via this.context.store) without threading it through props from
  // browser/index.js. connect(mapStateToProps) below also reads this same
  // context internally — contextType is just a second, independent read of it,
  // which is safe since Context consumption isn't exclusive.
  static contextType = ReactReduxContext;

  constructor(props) {
    super(props);
    var url = new URL(props.location);
    this.intelliUrl = url.searchParams.get("intelliUrl");
    this.trackingId = "GTM-K5WW8LV";
    if (props.location.includes("/tools/child-vaccination-growth-tracker") || props.location.includes("/tools/baby-meal-plan") || props.location.includes("/tools/breastfeeding-tracker") || props.location.includes("/memories") || props.location.includes("/tools/capture-baby-moments") || props.location.includes("/tools/baby-teething") || props.location.includes("/tools/baby-growth-and-development") || props.location.includes("/qna") || props.location.includes("/quiz") || props.location.includes("/baby-names") || props.location.includes("/baby-photo-contest")) {
      this.trackingId = "GTM-MPRDRG9";
    } else if (props.location.includes("/tools/fetal-movement-tracker") || props.location.includes('/tools/pregnancy-due-date-calculator') || props.location.includes("/tools/pregnancy-diet-plan") || props.location.includes("/tools/pregnancy-checkup-schedule") || props.location.includes("/tools/my-baby-bump") || props.location.includes("/tools/baby-size-comparison") || props.location.includes("/tools/fetal-development-video") || props.location.includes("/tools/pregnancy-week-by-week-tracker") || props.location.includes("/tools/pregnancy-week-by-week-guide") || props.location.includes("/tools/period-ovulation-calculator") || props.location.includes("/quiz") || props.location.includes("/discussions") || props.location.includes("/write-for-us") || props.location.includes("/groups") || props.location.includes("/quick-reads")) {
      this.trackingId = "GTM-PCKH594";
    } else if (props.location.includes('/tools') || props.location.includes('/online-consultation-prod') || props.location.includes('/questions-answers')) {
      this.trackingId = "GTM-5S69ZCN";
    }
  }

  componentDidMount() {
    const { pathname, search } = window.location;
    if (pathname.length > 1 && pathname.endsWith("/")) {
      const newPath = pathname.replace(/\/+$/, "");
      window.history.replaceState({}, "", newPath + search);
    }

    if (mountRemoteHeader) {
      mountRemoteHeader(
        { location: this.props.location, ...this.props.header },
        this.context.store
      );
    }

    TagManager.initialize({ gtmId: this.trackingId });

    if (navigator.userAgent) {
      global.userAgent = navigator.userAgent;
    }
    if (
      navigator.userAgent.match(/Android/i) ||
      navigator.userAgent.match(/webOS/i) ||
      navigator.userAgent.match(/iPhone/i) ||
      navigator.userAgent.match(/iPad/i) ||
      navigator.userAgent.match(/iPod/i) ||
      navigator.userAgent.match(/BlackBerry/i) ||
      navigator.userAgent.match(/Windows Phone/i)
    ) {
      global.isdesktop = false;
    }

    window.addEventListener("FC_LOGOUT", this.handleHostLogout);
    window.addEventListener("FC_SKIP_TO_MAIN", this.handleSkipToMain);
  }

  componentWillUnmount() {
    window.removeEventListener("FC_LOGOUT", this.handleHostLogout);
    window.removeEventListener("FC_SKIP_TO_MAIN", this.handleSkipToMain);
  }

  handleSkipToMain = () => {
    const el = document.getElementById("skipToH1");
    if (el) {
      el.setAttribute("tabindex", "0");
      el.focus();
    }
  };

  handleHostLogout = () => {
    console.log("📢 HOST CLIENT LOGOUT RECEIVED");
    Cookies.remove("FC_AUTH", { path: "/", domain: ".firstcry.com" });
    Cookies.remove("ftk");
    Cookies.remove("app_ftk");
    localStorage.removeItem("authData");
    global.userAry = "";
    global.ftk = "";
    global.accessToken = "";
    global.uID = "";
    global.clientId = "";
    this.props.dispatch({ type: "LOGOUT_USER" });
  };

  render() {
    var canonical_url = this.props.location;
    canonical_url = canonical_url.replace("http://", "https://");

    var linkAry = [];
    var MetaInfo = [];
    var _canonical_url = canonical_url.toLowerCase();
    _canonical_url = _canonical_url.split('?')[0];

    linkAry = [
      { "rel": "canonical", "href": _canonical_url }
    ];

    var _canonical_url = canonical_url.toLowerCase();
    _canonical_url = _canonical_url.split('?')[0];

    linkAry = [
      { "rel": "canonical", "href": _canonical_url }
    ];

    if (this.props.location.includes("/search-result/") && !this.props.location.includes("/search-result/all/")) {
      MetaInfo = [
        { "name": "robots", "content": "noindex" }
      ]
    }
    console.log("Rendering App component with location:", this.props.location);
    // console.log("this.props.onLogout::::::", this.props.onLogout);
    return (
      <div>
        <Helmet link={linkAry} meta={MetaInfo} />

        {/*
          SERVER: server/index.js pre-renders the federated header to a plain
            HTML string (renderToStaticMarkup) and passes it as `headerHtml` —
            it lands here verbatim, inside a real page render. ✅

          BROWSER: this div is hydrated with dangerouslySetInnerHTML, which React
            treats as opaque — it does not diff/replace these children during
            hydration, so the server-rendered header just stays put. Once the
            remote chunk loads, mountRemoteHeader (componentDidMount) hydrates
            this exact node (hydrateRoot, not createRoot — see the comment
            above the mountRemoteHeader definition) with the real, interactive
            component, reusing the existing markup instead of tearing it down.
            No appear/disappear/reappear flicker. ✅
        */}
        <div id="federated-header-root" suppressHydrationWarning dangerouslySetInnerHTML={{ __html: this.props.headerHtml || '' }} />

        <Routes>
          {routes.map((route, i) => (
            <Route
              key={i}
              path={route.path}
              element={
                <Provider value={this.props.initialData}>
                  {route.element}
                </Provider>
              }
            />
          ))}
        </Routes>

        <Footer />
      </div>
    );
  }
}

// Feeds the login state the host already resolved server-side (headerReducer,
// populated via setHeaderData in server/index.js) into the federated header as
// props, so its first SSR paint shows the correct logged-in/out state instead
// of a generic one that gets replaced once the remote chunk hydrates.
const mapStateToProps = (state) => ({
  header: state.header,
});

export default connect(mapStateToProps)(App);